// Route-level chunk prefetcher.
//
// React.lazy 預設只在 route 真正被造訪時才下載對應 chunk。
// 為了讓「切到下一頁」立即看到內容，這裡集中註冊所有 lazy import 並提供：
//   - prefetchRoute(path): hover/focus 時觸發對應 chunk 下載
//   - prefetchAllIdle(): App mount 之後在 requestIdleCallback 期間預載全部
//
// 注意：所有 import() 都是 idempotent — 重複呼叫不會重複下載，瀏覽器 / webpack runtime
// 會回傳同一個 module promise，所以可以安全地多次呼叫。

const loaders = {
  '/': () => import('../pages/DashboardSimple'),
  '/dashboard': () => import('../pages/Dashboard'),
  '/dashboard-simple': () => import('../pages/DashboardSimple'),
  '/profile': () => import('../pages/UserProfile'),
  '/quotations': () => import('../pages/QuotationList'),
  '/quotations/:id': () => import('../pages/QuotationDetail'),
  '/create': () => import('../pages/QuotationForm'),
  '/edit/:id': () => import('../pages/QuotationForm'),
  '/shipping/:id': () => import('../pages/ShippingDocumentPage'),
  '/invoices': () => import('../pages/InvoiceList'),
  '/invoices/:id': () => import('../pages/InvoiceDetail'),
  '/customers': () => import('../pages/CustomerList'),
  '/customers/new': () => import('../pages/CustomerForm'),
  '/customers/:id': () => import('../pages/CustomerDetail'),
  '/customers/:id/edit': () => import('../pages/CustomerForm'),
  '/assets': () => import('../pages/AssetList'),
  '/assets/new': () => import('../pages/AssetForm'),
  '/assets/:id': () => import('../pages/AssetDetail'),
  '/assets/:id/edit': () => import('../pages/AssetForm'),
  '/schedule': () => import('../pages/ScheduleCalendar'),
  '/message-templates': () => import('../pages/MessageTemplatesSettings'),
  '/company-settings': () => import('../pages/CompanySettings'),
  '/companies': () => import('../pages/CompanyManagement'),
  '/users': () => import('../pages/UserManagement'),
};

// 把任意內部 segment 換成 :id 之後嘗試 match loaders。Pure function — export 給測試用。
// 例：
//   /customers/abc          → /customers/:id
//   /customers/abc/edit     → /customers/:id/edit
//   /signature/abc          → /signature/:token（這條沒在 loaders，所以回 null）
export const matchKeyAgainst = (path, knownKeys) => {
  if (!path) return null;
  const set = knownKeys instanceof Set ? knownKeys : new Set(knownKeys);
  if (set.has(path)) return path;
  const segs = path.split('/').filter(Boolean);
  for (let len = segs.length; len > 0; len--) {
    const sub = segs.slice(0, len);
    const plain = '/' + sub.join('/');
    if (set.has(plain)) return plain;
    for (let j = 0; j < sub.length; j++) {
      const candidate = '/' + sub.map((s, idx) => (idx === j ? ':id' : s)).join('/');
      if (set.has(candidate)) return candidate;
    }
  }
  return null;
};

const matchKey = (path) => matchKeyAgainst(path, Object.keys(loaders));

export const prefetchRoute = (path) => {
  const key = matchKey(path);
  if (!key) return Promise.resolve();
  try {
    return loaders[key]();
  } catch {
    return Promise.resolve();
  }
};

const ric =
  typeof window !== 'undefined' && window.requestIdleCallback
    ? window.requestIdleCallback.bind(window)
    : (cb) => setTimeout(() => cb({ timeRemaining: () => 0 }), 500);

let allPrefetchScheduled = false;

const shouldSkipPrefetch = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return true;
  // Tab 不在前景時不勉強佔頻寬
  if (document.visibilityState && document.visibilityState !== 'visible') return true;
  // 使用者開了 data saver 或在慢網（4G 含 SaveData 模式）就跳過全量 prefetch
  const conn = navigator.connection;
  if (conn) {
    if (conn.saveData) return true;
    if (conn.effectiveType && /(^|-)2g$/.test(conn.effectiveType)) return true;
  }
  return false;
};

export const prefetchAllIdle = () => {
  if (allPrefetchScheduled) return;
  if (shouldSkipPrefetch()) return;
  allPrefetchScheduled = true;
  const keys = Object.keys(loaders);
  let i = 0;
  const step = () => {
    if (i >= keys.length) return;
    ric(() => {
      // 一次預載 3 個 chunk，避免一口氣占滿頻寬
      const batch = keys.slice(i, i + 3);
      i += 3;
      batch.forEach((k) => {
        try { loaders[k](); } catch {}
      });
      step();
    });
  };
  step();
};
